import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  ProviderEmployment,
  ProviderEmploymentEditResource,
  ProviderEmploymentViewResource,
  Permission,
} from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ProviderEmploymentPageBody } from "./ProviderEmploymentPageBody";
import { ProviderEmploymentPageHeader } from "./ProviderEmploymentPageHeader";

const routeName = "providerEmployment";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  providerId?: number = 0;
}

export const providerEmploymentPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Employment",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const ProviderEmploymentPage = () => {
  const ROOT_PERMISSION: Permission = Permission.ProviderEmployment;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const parentListContext = useContext<ListContextValue>(ListContext); // Access parent Provider list context to trigger reload after employment save
  const [employment, setEmployment] = useState<ProviderEmployment>({} as ProviderEmployment);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [employment] });
  const [viewResource, setViewResource] = useState<ProviderEmploymentViewResource>({});
  const [editResource, setEditResource] = useState<ProviderEmploymentEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.providerEmployments.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit")
      ApiService.providerEmployments.getEditResource().then((response) => {
        setEditResource(response);
      });
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${providerEmploymentPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`
    );

  const fetch = (id: number) => {
    ApiService.providerEmployments.get(id).then((response) => {
      if (id === 0 && optionalParams.providerId) response.providerId = optionalParams.providerId;

      setEmployment(response);
      resetUnsavedChanges([response]);
    });
  };

  const pageHeader = (
    <ProviderEmploymentPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={providerEmploymentPageRoute.handle}
      reroute={reroute}
      fetch={fetch}
      employment={employment}
      crudMode={params.crudMode}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
      parentListContext={parentListContext} // Pass parent list context down to header for reload
    />
  );

  const pageBody = (
    <ProviderEmploymentPageBody
      crudMode={params.crudMode}
      employment={employment}
      setEmployment={setEmployment}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
